import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { Button, Card, Input, Select, Space, Tooltip, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import request from "@/api";
import {
    exportSpecialZoneReservationOrderList,
    findSpecialZoneReservationOrderCount
} from "@/api/services/specialZoneReservationOrderService";
import { useUserStore } from "@/stores/react/authStore";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import {
    DateRangePicker as ZhDateRangePicker,
    Table as ZhTable,
    type PaginationProps,
    type TableColumnProps
} from "zhihao-ui-react";
import { displayZoneContractCustomerText } from "./zoneContract/zoneContractCustomerLabel";
import {
    aggregateReservationGoodsMetricsBySpec,
    displayApiDateTime,
    pickReservationTransportDisplay,
    reservationOrderStateLabel,
    ZONE_RESERVATION_MATERIAL_SPECS
} from "./zoneReservationOrder/zoneReservationDisplay";
import { formatMoney } from "@/utils/money";
import { openShippingTransportDetail } from "@/utils/shippingAdminNavigation";
import { notifyOperationSuccess } from "@/utils/operationFeedback";
import { ZONE_CONTRACT_RESERVATION_ORDER_STATE, ZONE_CONTRACT_SPECIAL_ZONE } from "@zhihaoscm/meta";
import type { SpecialZoneReservationOrderCountVo } from "@zhihaoscm/entity";

export interface ZoneReservationOrderRow {
    specialZoneReservationOrder?: {
        id?: string | number;
        specialZoneId?: number;
        shipName?: string;
        state?: number;
        createdTime?: string;
        transportOrderShipId?: string;
        requirementPlatId?: string;
    };
    specialZoneContract?: {
        id?: number;
        contractCode?: string;
        specialZoneName?: string;
        specialZoneId?: number;
        customerId?: number;
    };
    businessCustomer?: {
        id?: number;
        code?: string;
        applyState?: number;
        institutionName?: string;
        realName?: string;
        nickName?: string;
        mobile?: string;
    };
    buyerUserText?: string;
    ship?: { cnname?: string; name?: string };
    specialZoneReservationOrderGoods?: Array<{
        goodsCode?: number | string;
        plannedTonnage?: number;
        settlementTon?: number;
        settlementAmount?: number;
        unitPrice?: number;
    }>;
}

export interface ZoneReservationOrderResult {
    list?: ZoneReservationOrderRow[];
    total?: number;
    summary?: ZoneReservationOrderSummary;
}

export interface ZoneReservationOrderSummary {
    metricsBySpecId: Record<string, { plannedTonnage: number; settlementTon: number; settlementAmount: number }>;
}

export interface ZoneReservationOrderQuery {
    page: number;
    size: number;
    flag: 0 | 1;
    contractReservationKeyword?: string;
    customerKeyword?: string;
    shipOrderKeyword?: string;
    states?: number[];
    createStartDate?: string;
    createEndDate?: string;
}

export interface ZoneReservationOrderPageProps {
    exportOrders?: (query: ZoneReservationOrderExportQuery) => Promise<unknown>;
    loadOrders?: (query: ZoneReservationOrderQuery) => Promise<ZoneReservationOrderResult>;
    permissions?: string[];
}

export type ZoneReservationOrderExportQuery = Omit<ZoneReservationOrderQuery, "page" | "size">;

const defaultQuery: ZoneReservationOrderQuery = { page: 1, size: 10, flag: 1 };

/** 将 Vue 使用的全量汇总接口字段映射到三个固定物料分组。 */
function buildReservationSummaryFromCount(count: SpecialZoneReservationOrderCountVo): ZoneReservationOrderSummary {
    return {
        metricsBySpecId: {
            "1": {
                plannedTonnage: Number(count.sandPlannedTonnage) || 0,
                settlementTon: Number(count.sandSettlementTon) || 0,
                settlementAmount: Number(count.sandSettlementAmount) || 0
            },
            "2": {
                plannedTonnage: Number(count.melonPlannedTonnage) || 0,
                settlementTon: Number(count.melonSettlementTon) || 0,
                settlementAmount: Number(count.melonSettlementAmount) || 0
            },
            "3": {
                plannedTonnage: Number(count.stonePlannedTonnage) || 0,
                settlementTon: Number(count.stoneSettlementTon) || 0,
                settlementAmount: Number(count.stoneSettlementAmount) || 0
            }
        }
    };
}

/** 旧版 Element Plus 使用 20px 图标盒和更细的箭头路径，分页按钮需保留同一轮廓。 */
const reservationPaginationItemRender: NonNullable<PaginationProps["itemRender"]> = (_page, type, element) => {
    if (type !== "prev" && type !== "next") return element;

    const path =
        type === "prev"
            ? "M609.408 149.376 277.76 489.6a32 32 0 0 0 0 44.672l331.648 340.352a29.12 29.12 0 0 0 41.728 0 30.59 30.59 0 0 0 0-42.752L339.264 511.936l311.872-319.872a30.59 30.59 0 0 0 0-42.688 29.12 29.12 0 0 0-41.728 0"
            : "M340.864 149.312a30.59 30.59 0 0 0 0 42.752L652.736 512 340.864 831.872a30.59 30.59 0 0 0 0 42.752 29.12 29.12 0 0 0 41.728 0L714.24 534.336a32 32 0 0 0 0-44.672L382.592 149.376a29.12 29.12 0 0 0-41.728 0z";

    return (
        <svg aria-hidden="true" height="20" viewBox="0 0 1024 1024" width="20">
            <path d={path} fill="currentColor" />
        </svg>
    );
};

/** 旧版 Element Plus 下拉箭头的 20px SVG，避免 Ant 默认 12px 箭头产生轮廓差异。 */
const reservationSelectArrow = (
    <svg aria-hidden="true" height="20" viewBox="0 0 1024 1024" width="20">
        <path
            d="M831.872 340.864 512 652.672 192.128 340.864a30.59 30.59 0 0 0-42.752 0 29.12 29.12 0 0 0 0 41.6L489.664 714.24a32 32 0 0 0 44.672 0l340.288-331.712a29.12 29.12 0 0 0 0-41.728 30.59 30.59 0 0 0-42.752 0z"
            fill="currentColor"
        />
    </svg>
);

/** 复用 Vue 专区预约单分页接口。 */
export async function loadZoneReservationOrders(query: ZoneReservationOrderQuery): Promise<ZoneReservationOrderResult> {
    const { page: _page, size: _size, ...countQuery } = query;
    void _page;
    void _size;
    const [response, count] = await Promise.all([
        request.service.get<ZoneReservationOrderResult>("/admin/special-zone-reservation-order/paging", {
            params: query
        }),
        // 汇总接口异常时仍展示分页数据，并回退到当前页求和，保持列表主体可用。
        findSpecialZoneReservationOrderCount(countQuery).catch(() => undefined)
    ]);
    return {
        ...response.data,
        ...(count ? { summary: buildReservationSummaryFromCount(count) } : {})
    };
}

/** 复用 Vue 专区预约单导出接口，导出只提交当前已生效的筛选条件。 */
export async function exportZoneReservationOrders(query: ZoneReservationOrderExportQuery): Promise<unknown> {
    return exportSpecialZoneReservationOrderList(query);
}

/** 从 Vue 分页行中读取专区名称和船名的兼容字段。 */
function getZoneName(row: ZoneReservationOrderRow): string {
    const id = row.specialZoneReservationOrder?.specialZoneId ?? row.specialZoneContract?.specialZoneId;
    return (
        row.specialZoneContract?.specialZoneName ||
        ZONE_CONTRACT_SPECIAL_ZONE.list.find(item => Number(item.code) === Number(id))?.intro ||
        "--"
    );
}

/** 采购用户展示沿用旧 Vue 的企业名称、手机号和用户编码拼接规则。 */
function getBuyerName(row: ZoneReservationOrderRow): string {
    if (row.buyerUserText) return row.buyerUserText;
    const customer = row.businessCustomer;
    return displayZoneContractCustomerText(customer);
}

function getShipName(row: ZoneReservationOrderRow): string {
    return row.specialZoneReservationOrder?.shipName || row.ship?.cnname || row.ship?.name || "--";
}

/** Element Plus 每个单元格会包一层 .cell，普通文字也需保留同样的行盒和截断行为。 */
function reservationTextCell(value: string) {
    return <span className="zone-reservation-order-cell-text">{value}</span>;
}

/** 物料子列沿用 Vue 列表的数量/金额显示精度，并保留 0 的有效值。 */
function formatReservationMetric(value: number, field: "quantity" | "money"): string {
    return field === "money"
        ? formatMoney(value)
        : value.toLocaleString("zh-CN", { minimumFractionDigits: 0, maximumFractionDigits: 6 });
}

/** 汇总行不会继承叶子列对齐方式，使用满宽容器确保数值与 Vue footer 一样右对齐。 */
export function reservationSummaryCell(value: string, textAlign: "left" | "right" = "right") {
    return (
        <span
            className="zone-reservation-order-summary-cell"
            style={{
                display: "block",
                // 旧版 Element 汇总行最终使用无衬线字体，显式声明以统一中英文 glyph 度量。
                fontFamily: "sans-serif",
                textAlign,
                transform: textAlign === "right" ? "translateX(1px)" : undefined,
                width: "100%"
            }}
        >
            {value}
        </span>
    );
}

/** 专区预约单列表，保留 Vue 状态 Tab、筛选字段和详情入口。 */
export function ZoneReservationOrderPage({
    exportOrders = exportZoneReservationOrders,
    loadOrders = loadZoneReservationOrders,
    permissions
}: ZoneReservationOrderPageProps) {
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    // 路由页面从登录态读取权限，显式传入 permissions 的测试/嵌入调用仍优先。
    const permissionList = permissions ?? storePermissions;
    const canManage = permissionList.length === 0 || permissionList.includes("ROLE_ZONE_PREORDER_MANAGE");
    const canRead =
        permissionList.length === 0 ||
        permissionList.includes("ROLE_ZONE_PREORDER_R") ||
        permissionList.includes("ROLE_ZONE_PREORDER_MANAGE");
    const [query, setQuery] = useState<ZoneReservationOrderQuery>(defaultQuery);
    const [rows, setRows] = useState<ZoneReservationOrderRow[]>([]);
    const [total, setTotal] = useState(0);
    /** 生产数据源使用全量汇总；自定义测试数据源缺省时回退当前页计算。 */
    const [summary, setSummary] = useState<ZoneReservationOrderSummary | undefined>();
    /** 按状态缓存顶部统计卡片，切换页签时保留已加载的另一侧数量。 */
    const [stats, setStats] = useState({ effective: 0, cancelled: 0 });
    const [orderKeyword, setOrderKeyword] = useState("");
    const [userKeyword, setUserKeyword] = useState("");
    const [shipKeyword, setShipKeyword] = useState("");
    const [statusList, setStatusList] = useState<string[]>([]);
    const [createdDateRange, setCreatedDateRange] = useState<[string | null, string | null]>([null, null]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        let disposed = false;
        // 将请求调用放进 Promise 链，避免测试注入或异常适配器同步返回 undefined 时阻断错误态渲染。
        let requestResult: Promise<ZoneReservationOrderResult> | undefined;
        try {
            requestResult = loadOrders(query);
        } catch (error) {
            requestResult = Promise.reject(error);
        }
        void Promise.resolve(requestResult)
            .then(result => {
                if (disposed) return;
                if (!result) throw new Error("专区预约单响应为空");
                setRows(result.list || []);
                setTotal(result.total || 0);
                setSummary(result.summary);
                setStats(current => ({
                    ...current,
                    [query.flag === 1 ? "effective" : "cancelled"]: result.total || 0
                }));
                setErrorMessage(null);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("专区预约单加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadOrders, query]);

    /** 旧 Vue 首屏会并行查询有效和已取消两侧总数，保证第二张统计卡不会初始显示 0。 */
    useEffect(() => {
        // 自定义数据源由调用方控制请求次数；生产默认数据源才需要补拉另一侧统计。
        if (loadOrders !== loadZoneReservationOrders) return;
        let disposed = false;
        void loadOrders({ ...defaultQuery, flag: 0 })
            .then(result => {
                if (!disposed) setStats(current => ({ ...current, cancelled: Number(result.total) || 0 }));
            })
            .catch(() => undefined);
        return () => {
            disposed = true;
        };
    }, [loadOrders]);

    const search = () => {
        setQuery({
            ...defaultQuery,
            flag: query.flag,
            ...(orderKeyword.trim() ? { contractReservationKeyword: orderKeyword.trim() } : {}),
            ...(userKeyword.trim() ? { customerKeyword: userKeyword.trim() } : {}),
            ...(shipKeyword.trim() ? { shipOrderKeyword: shipKeyword.trim() } : {}),
            ...(statusList.length ? { states: statusList.map(Number) } : {}),
            ...(createdDateRange[0] ? { createStartDate: createdDateRange[0] } : {}),
            ...(createdDateRange[1] ? { createEndDate: createdDateRange[1] } : {})
        });
    };

    /** 表格筛选项由当前页面受控，重置时需同时清空草稿值并恢复当前状态页签的首屏查询。 */
    const resetFilters = () => {
        setOrderKeyword("");
        setUserKeyword("");
        setShipKeyword("");
        setStatusList([]);
        setCreatedDateRange([null, null]);
        setQuery({ ...defaultQuery, flag: query.flag });
    };

    const changeTab = (flag: 0 | 1) => setQuery({ ...defaultQuery, flag });
    /** 沿用 Vue 导出参数，排除分页字段，避免把当前页误当成导出范围。 */
    const exportList = async () => {
        const { page: _page, size: _size, ...exportQuery } = query;
        void _page;
        void _size;
        setExporting(true);
        try {
            await exportOrders(exportQuery);
            notifyOperationSuccess("导出成功");
            setErrorMessage(null);
        } catch {
            setErrorMessage("专区预约单导出失败");
        } finally {
            setExporting(false);
        }
    };
    // 将旧 Vue 表格字段映射为 zhihao-ui-react 的列配置，保持行数据和业务文案不变。
    const reservationColumns = useMemo<TableColumnProps<ZoneReservationOrderRow>[]>(
        () => [
            {
                label: "预约单编号",
                fixed: "left",
                // zhihao Table 会为固定列阴影补 31px，声明 173px 后最终渲染宽度与 Vue 的 204px 一致。
                width: 173,
                prop: "id",
                render: ({ row }) => {
                    const id = row.specialZoneReservationOrder?.id;
                    return id && canRead ? (
                        <Button
                            type="link"
                            onClick={() => navigate(`/tradeManage/zoneReservationOrderDetail?id=${id}`)}
                        >
                            {id}
                        </Button>
                    ) : (
                        id || "--"
                    );
                }
            },
            {
                label: "关联合同",
                fixed: "left",
                // 第二个固定列使用同一补偿值，确保 sticky 偏移与旧版 204px 列宽一致。
                width: 173,
                prop: "contract",
                render: ({ row }) => {
                    const contractCode = row.specialZoneContract?.contractCode;
                    return contractCode && canRead ? (
                        <Button
                            type="link"
                            onClick={() =>
                                navigate(`/tradeManage/zoneContractDetail?id=${row.specialZoneContract?.id}`)
                            }
                        >
                            {contractCode}
                        </Button>
                    ) : (
                        contractCode || "--"
                    );
                }
            },
            {
                label: "专区名称",
                width: 176,
                prop: "zone",
                render: ({ row }) => reservationTextCell(getZoneName(row))
            },
            // 旧版 Element Plus 为采购用户列设置固定 350px，避免宽表自动布局把列宽分配给该列。
            {
                label: "采购用户",
                width: 350,
                prop: "buyer",
                render: ({ row }) => reservationTextCell(getBuyerName(row))
            },
            {
                label: "船名",
                width: 176,
                prop: "ship",
                render: ({ row }) => reservationTextCell(getShipName(row))
            },
            {
                label: "船运单编号",
                width: 204,
                prop: "transport",
                render: ({ row }) => {
                    const transportOrderShipId = row.specialZoneReservationOrder?.transportOrderShipId;
                    const requirementPlatId = row.specialZoneReservationOrder?.requirementPlatId;
                    const transport = pickReservationTransportDisplay(transportOrderShipId, requirementPlatId);
                    return transport && canRead ? (
                        <Button
                            type="link"
                            onClick={() => void openShippingTransportDetail(transportOrderShipId, requirementPlatId)}
                        >
                            {transport}
                        </Button>
                    ) : (
                        transport || "--"
                    );
                }
            },
            ...ZONE_RESERVATION_MATERIAL_SPECS.map(spec => ({
                label: spec.name,
                prop: `material-${spec.id}`,
                children: [
                    {
                        label: "计划量 (吨)",
                        prop: `material-${spec.id}-planned`,
                        width: 190,
                        align: "right" as const,
                        render: ({ row }: { row: ZoneReservationOrderRow }) => {
                            const metrics = aggregateReservationGoodsMetricsBySpec(
                                row.specialZoneReservationOrderGoods as never,
                                ZONE_RESERVATION_MATERIAL_SPECS
                            )[spec.id];
                            return reservationTextCell(
                                formatReservationMetric(metrics?.plannedTonnage || 0, "quantity")
                            );
                        }
                    },
                    {
                        label: "结算吨数 (吨)",
                        prop: `material-${spec.id}-settlement-ton`,
                        width: 190,
                        align: "right" as const,
                        render: ({ row }: { row: ZoneReservationOrderRow }) => {
                            const metrics = aggregateReservationGoodsMetricsBySpec(
                                row.specialZoneReservationOrderGoods as never,
                                ZONE_RESERVATION_MATERIAL_SPECS
                            )[spec.id];
                            return reservationTextCell(
                                formatReservationMetric(metrics?.settlementTon || 0, "quantity")
                            );
                        }
                    },
                    {
                        label: "单价 (元)",
                        prop: `material-${spec.id}-unit-price`,
                        width: 190,
                        align: "right" as const,
                        render: ({ row }: { row: ZoneReservationOrderRow }) => {
                            const metrics = aggregateReservationGoodsMetricsBySpec(
                                row.specialZoneReservationOrderGoods as never,
                                ZONE_RESERVATION_MATERIAL_SPECS
                            )[spec.id];
                            return reservationTextCell(formatReservationMetric(metrics?.unitPrice || 0, "quantity"));
                        }
                    },
                    {
                        label: "结算金额 (元)",
                        prop: `material-${spec.id}-settlement-amount`,
                        width: 190,
                        align: "right" as const,
                        render: ({ row }: { row: ZoneReservationOrderRow }) => {
                            const metrics = aggregateReservationGoodsMetricsBySpec(
                                row.specialZoneReservationOrderGoods as never,
                                ZONE_RESERVATION_MATERIAL_SPECS
                            )[spec.id];
                            return reservationTextCell(
                                formatReservationMetric(metrics?.settlementAmount || 0, "money")
                            );
                        }
                    }
                ]
            })),
            {
                label: "创建时间",
                width: 188,
                prop: "created",
                render: ({ row }) =>
                    reservationTextCell(displayApiDateTime(row.specialZoneReservationOrder?.createdTime))
            },
            {
                label: "状态",
                width: 148,
                prop: "state",
                render: ({ row }) =>
                    reservationTextCell(reservationOrderStateLabel(row.specialZoneReservationOrder?.state))
            }
        ],
        [canRead, navigate]
    );

    /** 对齐 Vue ProTable：首列显示合计，物料列按当前列表数据汇总。 */
    const reservationSummary = ({
        columns,
        data
    }: {
        columns: TableColumnProps<ZoneReservationOrderRow>[];
        data: ZoneReservationOrderRow[];
    }) => {
        const pageMetricsBySpec = Object.fromEntries(
            ZONE_RESERVATION_MATERIAL_SPECS.map(spec => {
                const metrics = data.reduce(
                    (total, row) => {
                        const current = aggregateReservationGoodsMetricsBySpec(
                            row.specialZoneReservationOrderGoods as never,
                            ZONE_RESERVATION_MATERIAL_SPECS
                        )[spec.id];
                        return {
                            plannedTonnage: total.plannedTonnage + (current?.plannedTonnage || 0),
                            settlementTon: total.settlementTon + (current?.settlementTon || 0),
                            settlementAmount: total.settlementAmount + (current?.settlementAmount || 0)
                        };
                    },
                    { plannedTonnage: 0, settlementTon: 0, settlementAmount: 0 }
                );
                return [spec.id, metrics];
            })
        ) as Record<string, { plannedTonnage: number; settlementTon: number; settlementAmount: number }>;
        const metricsBySpec = summary?.metricsBySpecId || pageMetricsBySpec;

        return columns.map((column, index) => {
            if (index === 0) return reservationSummaryCell("合计:", "left");
            const property = String(column.prop || column.property || "");
            const match = property.match(/^material-(\d+)-(planned|settlement-ton|settlement-amount)$/);
            if (!match) return null;
            const metrics = metricsBySpec[match[1]!];
            if (!metrics) return null;
            const value =
                match[2] === "planned"
                    ? formatReservationMetric(metrics.plannedTonnage, "quantity")
                    : match[2] === "settlement-ton"
                      ? formatReservationMetric(metrics.settlementTon, "quantity")
                      : formatReservationMetric(metrics.settlementAmount, "money");
            return reservationSummaryCell(value);
        });
    };

    return (
        <section aria-label="zone-reservation-order-page" className="admin-trade-list-page">
            <Card>
                <ZhTable
                    columns={reservationColumns}
                    data={rows}
                    pagination={false}
                    rowKey={row => String(row.specialZoneReservationOrder?.id)}
                    maxHeight="calc(100vh - 428px)"
                    showSummary
                    summaryMethod={reservationSummary}
                    sumText="合计:"
                    // 分组宽表使用显式列宽和 fixed 布局，确保表头、表体与汇总行共用同一列几何。
                    tableLayout="fixed"
                    // 受控筛选使用页面级重置入口，避免表格内部 reset 只能清空自己的搜索模型。
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                专区预约单
                            </Typography.Title>

                            <div aria-label="专区预约单状态页签" className="admin-trade-stat-cards" role="tablist">
                                <button
                                    aria-selected={query.flag === 1}
                                    aria-label="有效预约单"
                                    className={`admin-trade-stat-card is-selectable ${query.flag === 1 ? "is-active" : ""}`}
                                    role="tab"
                                    type="button"
                                    onClick={() => changeTab(1)}
                                >
                                    <div className="admin-trade-stat-card__label">有效预约单</div>
                                    <div className="admin-trade-stat-card__value admin-trade-stat-card__value--zone">
                                        {stats.effective}
                                    </div>
                                </button>
                                <button
                                    aria-selected={query.flag === 0}
                                    aria-label="已取消预约单"
                                    className={`admin-trade-stat-card is-selectable is-cancelled ${query.flag === 0 ? "is-active" : ""}`}
                                    role="tab"
                                    type="button"
                                    onClick={() => changeTab(0)}
                                >
                                    <div className="admin-trade-stat-card__label">已取消预约单</div>
                                    <div className="admin-trade-stat-card__value admin-trade-stat-card__value--zone">
                                        {stats.cancelled}
                                    </div>
                                </button>
                            </div>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <Space className="admin-table-filter-form mb-4" wrap>
                                {canManage ? (
                                    <Button
                                        aria-label="导出专区预约单"
                                        className="zone-reservation-order-export"
                                        type="primary"
                                        loading={exporting}
                                        onClick={() => void exportList()}
                                    >
                                        导出
                                    </Button>
                                ) : null}
                                <Input
                                    aria-label="预约单或合同关键词"
                                    className="zone-reservation-order-keyword"
                                    placeholder="请输入预约单编号/合同编号搜索"
                                    value={orderKeyword}
                                    onChange={event => setOrderKeyword(event.target.value)}
                                />
                                <Input
                                    aria-label="采购用户关键词"
                                    className="zone-reservation-order-customer"
                                    placeholder="请输入用户ID/个人或机构实名/手机号搜索"
                                    value={userKeyword}
                                    onChange={event => setUserKeyword(event.target.value)}
                                />
                                <Input
                                    aria-label="船名或船运单关键词"
                                    className="zone-reservation-order-ship"
                                    placeholder="请输入船名/船运单编号搜索"
                                    value={shipKeyword}
                                    onChange={event => setShipKeyword(event.target.value)}
                                />
                                <Select
                                    aria-label="预约单状态"
                                    className="zone-reservation-order-state"
                                    maxTagCount={1}
                                    mode="multiple"
                                    options={ZONE_CONTRACT_RESERVATION_ORDER_STATE.list.map(item => ({
                                        label: item.intro,
                                        value: String(item.code)
                                    }))}
                                    placeholder="请选择状态"
                                    suffixIcon={reservationSelectArrow}
                                    value={statusList}
                                    onChange={setStatusList}
                                />
                                <ZhDateRangePicker
                                    aria-label="创建日期"
                                    className="zone-reservation-order-date"
                                    endPlaceholder="创建结束日期"
                                    format="YYYY-MM-DD"
                                    rangeSeparator="-"
                                    startPlaceholder="创建开始日期"
                                    type="daterange"
                                    value={createdDateRange}
                                    valueFormat="YYYY-MM-DD"
                                    onChange={(_value, formattedValue) =>
                                        setCreatedDateRange(
                                            formattedValue
                                                ? [formattedValue[0] || null, formattedValue[1] || null]
                                                : [null, null]
                                        )
                                    }
                                />
                                <Button
                                    aria-label="查询预约单"
                                    className="admin-table-search-action zone-reservation-order-search"
                                    icon={<TableSearchIcon name="search" />}
                                    type="primary"
                                    onClick={search}
                                >
                                    搜索
                                </Button>
                                <Tooltip
                                    classNames={{ root: "zone-reservation-order-reset-tooltip" }}
                                    placement="top"
                                    title="重置"
                                >
                                    <Button
                                        aria-label="重置"
                                        className="table-filter-reset-button"
                                        icon={<TableSearchIcon name="reset" />}
                                        onClick={resetFilters}
                                    />
                                </Tooltip>
                            </Space>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                className="mt-4 self-end"
                                current={query.page}
                                pageSize={query.size}
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                itemRender={reservationPaginationItemRender}
                                locale={{ jump_to: "转到" }}
                                showSizeChanger
                                total={total}
                                onChange={(page, size) =>
                                    // 对齐旧 Vue ProTable：切换每页条数后回到第一页，普通翻页保留当前页大小。
                                    setQuery(current => ({ ...current, page: size !== current.size ? 1 : page, size }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}

export default ZoneReservationOrderPage;
